import { useEffect, useRef, useState } from "react";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { TouchPoint } from "../types";
import Toggle from "./Toggle";

function TouchCanvas(props: {
  touches: any;
  setTouches: any;
  minSpeed: any;
  setMinSpeed: any;
  maxSpeed: any;
  setMaxSpeed: any;
  colors: any;
  setColors: any;
}) {
  const [showSpeedProfile, setShowSpeedProfile] = useState(false);
  const [currentTouches, setCurrentTouches] = useState<TouchPoint[]>([]);

  const canvasRef = useRef(null);

  useBodyScrollLock();

  const handleTouchStart = (e: any) => {
    e.preventDefault();
    logChangedTouches(e.changedTouches, "start");
  };

  const handleTouchMove = (e: any) => {
    e.preventDefault();
    logChangedTouches(e.touches, "move");
  };

  const handleTouchEnd = (e: any) => {
    e.preventDefault();
    logChangedTouches(e.changedTouches, "end");

    for (var i = 0; i < e.changedTouches.length; i++) {
      releaseColorId(e.changedTouches[i].identifier);
    }
  };

  const handleTouchCancel = (e: any) => {
    logChangedTouches(e.changedTouches, "end");
    props.setColors([-1, -1, -1, -1, -1]);
  };

  const logChangedTouches = (changedTouches: any, eventType: string) => {
    let changes = [];
    let current = [];

    let canvas = document.getElementById("canvas");

    let offset = getOffset(canvas);

    for (var i = 0; i < changedTouches.length; i++) {
      let touch: TouchPoint = {
        pointerId: changedTouches[i].identifier,
        colorId: getColorId(changedTouches[i].identifier),
        event: eventType,
        x: changedTouches[i].clientX - offset.left,
        y: changedTouches[i].clientY - offset.top,
        rx: changedTouches[i].radiusX,
        ry: changedTouches[i].radiusY,
        timestamp: Date.now(),
      };
      changes.push(touch);
      current.push(touch);
    }

    if (props.touches) {
      let tmp = props.touches;
      props.setTouches(tmp.concat(changes));
    } else {
      props.setTouches(changes);
    }

    setCurrentTouches(current);
  };

  const getColorId = (identifier: number) => {
    let tmp = props.colors;

    // get colorId if id is already known
    let existingIndex = props.colors.findIndex((id: number) => {
      return id === identifier;
    });
    if (existingIndex !== -1) {
      return existingIndex;
    }

    // get first free colorId
    for (var i = 0; i < 5; i++) {
      if (tmp[i] === -1) {
        tmp[i] = identifier;
        props.setColors(tmp);
        return i;
      }
    }
    return 0;
  };

  const releaseColorId = (identifier: number) => {
    let tmp = props.colors;

    let index = props.colors.findIndex((id: number) => id === identifier);
    tmp[index] = -1;

    props.setColors(tmp);
  };

  const getOffset = (el: HTMLElement | null) => {
    if (!el) return { left: 0, top: 0 };

    const rect = el.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
    };
  };

  const getUniqueTouchIds = () => {
    const uniqueIds: number[] = [];

    props.touches?.map((touch: { pointerId: number }) => {
      if (uniqueIds.indexOf(touch.pointerId) === -1) {
        uniqueIds.push(touch.pointerId);
      }
      return null;
    });

    return uniqueIds ? uniqueIds : [];
  };

  const getTouchListForID = (touchId: number) => {
    let touchList = props.touches?.filter(
      (touchPoint: { pointerId: number }) => touchPoint.pointerId === touchId
    );

    // sort by timestamp
    touchList.sort((a: TouchPoint, b: TouchPoint) => {
      return a.timestamp - b.timestamp;
    });

    // calculate speed and add to objects
    for (var i = 0; i < touchList.length; i++) {
      let speed = 0;
      if (i === 0) {
        speed = 0;
      } else {
        speed = getSegmentSpeed(touchList[i - 1], touchList[i]);
      }
      touchList[i].speed = speed;
    }

    return touchList ? touchList : [];
  };

  const getSegmentDistance = (
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) => {
    let xs = x2 - x1;
    let ys = y2 - y1;

    xs *= xs;
    ys *= ys;

    return Math.sqrt(xs + ys);
  };

  const getSegmentDuration = (t1: number, t2: number) => {
    return t2 - t1;
  };

  const getSegmentSpeed = (p1: TouchPoint, p2: TouchPoint) => {
    let speed =
      getSegmentDistance(p1.x, p1.y, p2.x, p2.y) /
      getSegmentDuration(p1.timestamp, p2.timestamp);

    updateMinMaxSpeed(speed);

    return speed;
  };

  const updateMinMaxSpeed = (speed: number) => {
    if (props.minSpeed === undefined || props.minSpeed > speed) {
      props.setMinSpeed(speed);
    }
    if (props.maxSpeed === undefined || props.maxSpeed < speed) {
      props.setMaxSpeed(speed);
    }
  };

  const getStrokeColorBySpeed = (speed: number, opacity: number = 1) => {
    if (props.minSpeed === undefined || props.maxSpeed === undefined) {
      return "black";
    }

    let colPos: number =
      3 - ((speed - props.minSpeed) / (props.maxSpeed - props.minSpeed)) * 3;

    let color: string;

    if (colPos < 1) {
      // red to yellow
      color = rgba(255, colPos * 255, 0, opacity);
    } else if (colPos < 2) {
      // yellow to green
      color = rgba(255 - (colPos - 1) * 255, 255, 0, opacity);
    } else {
      // green to blue
      color = rgba(0, 255 - (colPos - 2) * 255, (colPos - 2) * 255, opacity);
    }

    return color;
  };

  const getStrokeColorByColorId = (colorId: number, opacity: number = 1) => {
    const colors = [
      "rgba(236, 70, 73," + opacity + ")", // rot
      "rgba(82, 220, 131," + opacity + ")", // grün
      "rgba(155, 27, 78," + opacity + ")", // lila
      "rgba(251, 210, 89," + opacity + ")", // gelb
      "rgba(65, 190, 245," + opacity + ")", // blau
    ];

    return colors[colorId % colors.length];
  };

  const rgba = (r: number, g: number, b: number, opacity: number = 1) => {
    return "rgba(" + r + "," + g + "," + b + "," + opacity + ")";
  };

  const draw = () => {
    const canvasObject = canvasRef.current;
    if (canvasObject != null) {
      const canvas: HTMLCanvasElement = canvasObject;
      const ctx = canvas.getContext("2d");

      const touchIds = getUniqueTouchIds();

      if (ctx) {
        clearCanvas();

        // draw currentTouches
        if (currentTouches) {
          for (var k = 0; k < currentTouches.length; k++) {
            if (currentTouches[k].event !== "end") {
              drawFullCircle(ctx, currentTouches[k], 50, 0.4);
              drawCrossHair(ctx, currentTouches[k]);
            }
          }
        }

        // foreach touchId
        for (var i = 0; i < touchIds.length; i++) {
          // get all touchPoints
          let points = getTouchListForID(touchIds[i]);
          if (points) {
            for (var j = 0; j < points.length; j++) {
              // draw start
              if (points[j].event === "start") {
                drawCircle(ctx, points[j], 15, 0.3);
              }

              // draw point
              drawCircle(ctx, points[j], 2, 0.3);

              if (points[j].event === "move") {
                // draw segment
                ctx.beginPath();
                ctx.moveTo(points[j - 1].x, points[j - 1].y);
                ctx.lineTo(points[j].x, points[j].y);
                ctx.strokeStyle = showSpeedProfile
                  ? getStrokeColorBySpeed(points[j].speed)
                  : getStrokeColorByColorId(points[j].colorId);
                ctx.lineCap = "round";
                ctx.lineWidth = 1;
                ctx.stroke();
              }

              // draw end
              if (points[j].event === "end") {
                drawCross(ctx, points[j], 10, 0.3);
              }
            }
          }
        }
      }
    }
  };

  const drawFullCircle = (
    ctx: CanvasRenderingContext2D,
    point: TouchPoint,
    radius: number,
    opacity: number = 1
  ) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI, false);
    ctx.lineWidth = 5;
    ctx.strokeStyle = getStrokeColorByColorId(point.colorId, opacity);
    ctx.fillStyle = getStrokeColorByColorId(point.colorId, opacity);
    ctx.fill();
    ctx.stroke();
  };

  const drawCircle = (
    ctx: CanvasRenderingContext2D,
    point: TouchPoint,
    radius: number,
    opacity: number = 1
  ) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI, false);
    ctx.lineWidth = 1;
    ctx.strokeStyle = getStrokeColorByColorId(point.colorId, opacity);
    ctx.stroke();
  };

  const drawCross = (
    ctx: CanvasRenderingContext2D,
    point: TouchPoint,
    radius: number,
    opacity: number = 1
  ) => {
    ctx.beginPath();
    ctx.moveTo(point.x - radius, point.y - radius);
    ctx.lineTo(point.x + radius, point.y + radius);
    ctx.lineWidth = 1;
    ctx.strokeStyle = getStrokeColorByColorId(point.colorId, opacity);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(point.x - radius, point.y + radius);
    ctx.lineTo(point.x + radius, point.y - radius);
    ctx.lineWidth = 1;
    ctx.strokeStyle = getStrokeColorByColorId(point.colorId, opacity);
    ctx.stroke();
  };

  const drawCrossHair = (ctx: CanvasRenderingContext2D, point: TouchPoint) => {
    // x
    ctx.beginPath();
    ctx.moveTo(point.x, 0);
    ctx.lineTo(point.x, window.screen.height);
    ctx.lineWidth = 1;
    ctx.strokeStyle = rgba(0, 0, 0, 0.3);
    ctx.stroke();

    // y
    ctx.beginPath();
    ctx.moveTo(0, point.y);
    ctx.lineTo(window.screen.width, point.y);
    ctx.lineWidth = 1;
    ctx.strokeStyle = rgba(0, 0, 0, 0.3);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvasObject = canvasRef.current;
    if (canvasObject != null) {
      const canvas: HTMLCanvasElement = canvasObject;

      let rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  useEffect(() => {
    draw();
  }, [props.touches, showSpeedProfile]);

  return (
    <div
      className="absolute top-0 overflow-hidden"
      style={{ touchAction: "none" }}
    >
      <canvas
        ref={canvasRef}
        id="canvas"
        className="w-screen h-screen overflow-hidden no-select"
        // className="flex flex-grow no-select"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{ userSelect: "none", touchAction: "none" }}
      ></canvas>
      <div className="absolute bottom-4 left-2 no-user-interaction">
        <Toggle
          title="show speed profile"
          checked={showSpeedProfile}
          onChange={setShowSpeedProfile}
        />
      </div>
    </div>
  );
}

export default TouchCanvas;
